import { collectComposerInlineTokens } from "@amoreg/shared/composerInlineTokens";
import { collectUserMentions } from "./reviewMentions";
import { UserMention } from "./UserMention";
import { PolicyMentionCard } from "../chat/PolicyMentionCard";
import { FileTextIcon, SparklesIcon } from "lucide-react";
import type { ReactNode } from "react";
import { ConnectorIcon } from "../connections/ConnectorIcon";
import { resolveConnectorAccent } from "../../lib/connectorAccent";
import { useConnectorAccentFamily } from "../../state/connectorAccent";

export function ReviewCommentText({ text }: { text: string }) {
  const users = collectUserMentions(`${text} `);
  const tokens = [
    ...collectComposerInlineTokens(`${text} `).filter(
      (token) =>
        (token.type === "policy" || token.type === "skill" || token.type === "connection") &&
        !users.some((user) => token.start < user.end && token.end > user.start),
    ),
    ...users,
  ].sort((a, b) => a.start - b.start);
  const parts: ReactNode[] = [];
  let cursor = 0;
  for (const token of tokens) {
    if (token.start < cursor) continue;
    parts.push(text.slice(cursor, token.start));
    parts.push(
      token.type === "user" ? (
        <UserMention key={token.start} userId={token.userId} label={token.label} />
      ) : token.type === "policy" ? (
        <PolicyMentionCard key={token.start} policyId={token.value} label={token.label}>
          <span className="inline-flex max-w-full items-center gap-1 rounded-md bg-muted px-1.5 py-0.5 align-middle text-xs font-medium">
            <FileTextIcon className="size-3.5 shrink-0" />
            <span className="truncate">{token.label}</span>
          </span>
        </PolicyMentionCard>
      ) : token.type === "connection" ? (
        <ConnectionChip
          key={token.start}
          connectorId={token.value}
          scope={token.scope}
          label={token.label}
        />
      ) : (
        <span
          key={token.start}
          className="inline-flex items-center gap-1 rounded-md bg-violet-500/10 px-1.5 py-0.5 align-middle text-xs text-violet-700 dark:text-violet-300"
        >
          <SparklesIcon className="size-3.5" />${token.value}
        </span>
      ),
    );
    cursor = token.end;
  }
  parts.push(text.slice(cursor));
  return <>{parts}</>;
}

function ConnectionChip(props: {
  connectorId: string;
  scope: "personal" | "workspace";
  label: string;
}) {
  const accent = resolveConnectorAccent(useConnectorAccentFamily(props.scope, props.connectorId));
  return (
    <span
      className={`inline-flex max-w-full items-center gap-1 rounded-md border px-1.5 py-0.5 align-middle text-xs font-medium ${accent.chip}`}
    >
      <span className="flex size-3.5 shrink-0 items-center justify-center">
        <ConnectorIcon connection={{ id: props.connectorId, iconUrl: null, toolkitSlug: null }} />
      </span>
      <span className="truncate">{props.label}</span>
    </span>
  );
}
